import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Copy, ImageIcon, Loader2, Radio } from "lucide-react";

const abs = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function CaptionBlock({ id, title, text, note }) {
  return (
    <div data-testid={`caption-${id}`} className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{title}</div>
        <button
          data-testid={`caption-${id}-copy`}
          onClick={() => { navigator.clipboard.writeText(text); toast.success("Caption copied"); }}
          className="text-slate-500 hover:text-blue-700 transition-colors"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">{text}</p>
      {note && <p className="text-[11px] text-amber-700 mt-1.5">{note}</p>}
    </div>
  );
}

function CompanionBlock({ contentId, companion }) {
  const [url, setUrl] = useState(companion?.image_url);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    const needsArt = !companion?.illustration_url;
    if (needsArt && !window.confirm("This generates one paid Nano Banana illustration (Universal Key). Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/studio/content/${contentId}/companion-image`, { generate_illustration: needsArt });
      setUrl(data.image_url);
      toast.success("Companion image ready (4:5)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Companion image failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="companion-panel" className="rounded-lg border border-slate-200 p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Companion image · 4:5</div>
      {url ? (
        <a data-testid="companion-download" href={abs(url)} download className="block mt-2">
          <img src={abs(url)} alt="Companion" className="w-40 rounded border border-slate-200" />
        </a>
      ) : (
        <p className="text-xs text-slate-500 mt-1.5">Not created yet. Text is drawn deterministically over the illustration.</p>
      )}
      <Button data-testid="companion-create-btn" size="sm" variant="outline" onClick={make} disabled={busy} className="mt-2 h-8 text-xs">
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5 mr-1.5" />}
        {url ? "Re-compose image" : "Create companion image"}
      </Button>
    </div>
  );
}

function ConnectionBlock() {
  const [conn, setConn] = useState(null);
  useEffect(() => { api.get("/publishing/connections").then((r) => setConn(r.data)).catch(() => setConn(null)); }, []);
  if (!conn) return null;
  return (
    <div data-testid="distribution-status" className="rounded-lg border border-slate-200 p-3 flex items-start gap-2">
      <Radio className={`w-4 h-4 mt-0.5 ${conn.real_publishing_available ? "text-emerald-600" : "text-amber-600"}`} />
      <div className="text-xs text-slate-600">
        <div className="font-semibold text-slate-800">
          Facebook / Instagram: {conn.real_publishing_available ? "connected" : "not connected — publishing is simulated only"}
        </div>
        <div className="mt-0.5">Nothing is posted from here until an authorised Meta connection exists.</div>
      </div>
    </div>
  );
}

export const StoryAssetsPanel = ({ content }) => {
  const caps = content.captions || {};
  return (
    <div data-testid="story-assets-panel" className="rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// Distribution assets</div>
      {content.fictional_label && (
        <div data-testid="fictional-label" className="text-xs text-slate-600">On-screen label: <b>{content.fictional_label}</b></div>
      )}
      <CaptionBlock id="facebook" title="Facebook caption" text={caps.facebook || content.caption} />
      <CaptionBlock
        id="instagram"
        title="Instagram caption"
        text={caps.instagram_bio_link_verified ? caps.instagram_link_in_bio : caps.instagram || content.caption}
        note={caps.instagram_bio_link_verified ? null : "\"Link in bio\" variant unlocks once the bio link is verified."}
      />
      <CompanionBlock contentId={content.id} companion={content.companion} />
      <ConnectionBlock />
    </div>
  );
};
